/* WENNEBIRD fitment helpers. Catalog labels are not physical-fit evidence. */
(() => {
  'use strict';
  const yearPattern = /\b(?:19|20)\d{2}\b/;
  function ranges(value) {
    const source = String(value || '').normalize('NFKC')
      .replace(/[\u2010-\u2015\u2212]/g, '-')
      .split(/\b(?:not\s+(?:fit|for)|exclud(?:es?|ing)|except)\b/i)[0];
    const isYear = (text) => /^(?:19|20)\d{2}$/.test(text);
    // Reject malformed numeric endpoints before a valid year can be extracted
    // from the tail (for example, "202-2026" must not mean "2026").
    for (const pair of source.matchAll(/\b(\d{1,5})\s*(?:-|to)\s*(\d{1,5})\b/gi)) {
      if ((isYear(pair[1]) || isYear(pair[2])) &&
          (!isYear(pair[1]) || !isYear(pair[2]) || Number(pair[1]) > Number(pair[2]))) return [];
    }
    const found = [];
    const pattern = /\b((?:19|20)\d{2})(?:\s*(?:-|to)\s*((?:19|20)\d{2}))?\b/gi;
    for (const match of source.matchAll(pattern)) {
      const from = Number(match[1]);
      const to = Number(match[2] || match[1]);
      const tail = source.slice(match.index + match[0].length);
      if (from > to || /^\s*(?:\+|-\s*(?:\d|$)|to\b)/i.test(tail)) return [];
      found.push([from, to]);
    }
    return found.filter(([a, b], i) => found.findIndex(([c, d]) => a === c && b === d) === i);
  }
  const supports = (list, year) => Number.isInteger(year) && Array.isArray(list) &&
    list.some((pair) => Array.isArray(pair) && pair.length === 2 &&
      Number.isInteger(pair[0]) && Number.isInteger(pair[1]) && year >= pair[0] && year <= pair[1]);
  function variantRanges(variant, title) {
    const own = String(variant?.title || '');
    return yearPattern.test(own) ? ranges(own) : ranges(title);
  }
  function defaultOption(options, requestedYear, handle) {
    const list = Array.from(options || []).filter((o) => !o.disabled && o.value !== '');
    if (requestedYear !== null && requestedYear !== undefined) {
      if (!Number.isInteger(requestedYear)) return null;
      return list.find((o) => supports(ranges(o.textContent), requestedYear)) || null;
    }
    if (/2019-2024.*bmw-3-series-m3/i.test(handle || '')) {
      return list.find((o) => /2019-2026/.test(o.textContent) && /3-Pc Set/i.test(o.textContent)) || null;
    }
    return null;
  }
  const stripVariantColor = (value) => String(value || '')
    .replace(/\s*\/\s*(?:black|gr[ae]y|beige)\s*$/i, '').trim();
  const coverageVehicleText = (variant, spec, productTitle = '') =>
    [productTitle, variant?.title || '', ...(spec?.models || []).map((m) => m.name || '')].join(' ');
  const fullCabAlways = (variant, spec, productTitle = '') => {
    const modelText = (spec?.models || []).map((m) => m.name || '').join(' ');
    const source = modelText || coverageVehicleText(variant, spec, productTitle);
    return /(?:Mercedes-Benz|Freightliner)\s+Sprinter|Ram\s+ProMaster|Ford\s+Transit(?:\b|$)|Chevrolet\s+Express(?:\s+Van)?/i.test(source);
  };
  const fullCabSelectedSet = (variant, spec, productTitle = '') =>
    /Freightliner|Kenworth|Peterbilt/i.test(
      (spec?.models || []).map((m) => m.name || '').join(' ') || coverageVehicleText(variant, spec, productTitle)
    );
  function pieceCount(variant, spec) {
    const coverage = String(spec?.coverage || '');
    let match = coverage.match(/;\s*(\d+)\s+pieces?/i);
    if (match) return Number(match[1]);
    match = stripVariantColor(variant?.title).match(/\b(\d+)\s*-?\s*Pc\b/i);
    return match ? Number(match[1]) : null;
  }
  function seatCount(variant, spec) {
    const text = [...(spec?.conditions || []), stripVariantColor(variant?.title)].join(' ');
    const match = text.match(/\b([5-8])\s+(?:seater|seats?)\b/i);
    return match ? Number(match[1]) : null;
  }
  function verifiedLegacySelectedScope(variant, productTitle = '') {
    const text = stripVariantColor(variant?.title);
    const product = String(productTitle || '');

    // Audited legacy selected-set labels whose physical coverage is proven by
    // same-year sibling variants (Front + row-specific pieces) or the product's
    // explicit legacy collection role. These only describe coverage; fitment
    // review flags remain authoritative and can still block ordering.
    if (/Toyota Highlander/i.test(product) && /Highlander\s*·\s*3-Pc Set/i.test(text)) return 'rows_1_2';
    if (/Ford Explorer/i.test(product) && /2020-2026 Explorer\s*·\s*3-Pc Set/i.test(text)) return 'rows_1_2';
    if (/Land Rover Range Rover \(Not fit LWB\/EWB\)/i.test(product) && /Range Rover\s*·\s*3-Pc Set/i.test(text)) return 'full_cabin';
    if (/Mercedes-Benz S-Class Sedan/i.test(product) && /2021-2026 S-Class\s*·\s*3-Pc Set/i.test(text)) return 'full_cabin';
    if (/Lexus GX 460/i.test(product) && /GX460\s*·\s*3-Pc Set/i.test(text)) return 'rows_1_2';
    if (/Chevrolet Tahoe/i.test(product) && /Tahoe\s*·\s*2-Pc Set/i.test(text)) return 'front_row';
    if (/2015-2016 Ford F-150 SuperCrew Cab without Fold-Flat Storage/i.test(product) &&
        /F-150 SuperCrew without Fold-Flat\s*·\s*1-Pc Set/i.test(text)) return 'rear_row';
    return '';
  }

  function coverageScope(variant, spec, context = {}, productTitle = '') {
    const text = stripVariantColor(variant?.title);
    const coverage = String(spec?.coverage || '');
    const notes = (spec?.notes || []).join(' ');
    const pieces = pieceCount(variant, spec);

    if (/^Full-cabin mat/i.test(coverage)) return 'full_cabin';
    if (/^Full-cab mat/i.test(coverage)) return 'full_cab';

    const verifiedLegacyScope = verifiedLegacySelectedScope(variant, productTitle);
    const reviewBlocksFull = (spec?.review || 0) > 0 && /^full_/.test(verifiedLegacyScope);
    if (verifiedLegacyScope && !reviewBlocksFull) return verifiedLegacyScope;

    // Commercial vans whose mat set is designed around the complete cab floor.
    // Coverage scope is independent of piece count: 1Pc => Mat, 2Pc+ => Mats.
    const explicitNonCabFull = /Rear-row|Second-row|Third-row|First- and second-row|Second- and third-row|Driver-side|Passenger-side|Cargo|Trunk/i.test(coverage) ||
      /\b(?:Rear\s*(?:Row|Mat)|2nd\s+Rows?|3rd\s+Row|1st\s*&\s*2nd\s+Rows?|2nd\s*&\s*3rd\s+Rows?|Driver(?:[- ]Side|'s)?|Passenger(?:[- ]Side|'s)?|Cargo|Trunk)\b/i.test(text);
    if (fullCabAlways(variant, spec, productTitle) && !explicitNonCabFull) return 'full_cab';

    if (/Driver-side/i.test(coverage) || /\bDriver(?:[- ]Side|'s)?\b/i.test(text)) return 'driver_side';
    if (/Passenger-side/i.test(coverage) || /\bPassenger(?:[- ]Side|'s)?\b/i.test(text)) return 'passenger_side';
    if (/First- and second-row/i.test(coverage) || /\b1st\s*&\s*2nd\s+Rows?\b/i.test(text)) return 'rows_1_2';
    if (/Second- and third-row/i.test(coverage) || /\b2nd\s*&\s*3rd\s+Rows?\b/i.test(text)) return 'rows_2_3';
    if (/Third-row/i.test(coverage) || /\b3rd\s+Row\b/i.test(text)) return 'third_row';
    if (/Second-row/i.test(coverage) || /\b2nd\s+Rows?\b/i.test(text)) return 'second_row';
    if (/Rear-row/i.test(coverage) || /\bRear\s*(?:Row|Mat)\b/i.test(text)) return 'rear_row';
    if (/Front-row/i.test(coverage) || /\bFront\s*(?:Row|Mat)\b/i.test(text) || /\bFront\s*\u00b7?\s*2\s*-?\s*Pc/i.test(text)) return 'front_row';
    if (/Cargo|Trunk/i.test(coverage + ' ' + text)) return 'cargo_area';

    if (/^Selected mat set;/i.test(coverage)) {
      // Review-required records always stay conservative, including pickup/truck families.
      if ((spec?.review || 0) > 0) return 'selected_set';
      if (fullCabSelectedSet(variant, spec, productTitle) && pieces >= 2) return 'full_cab';

      // An explicit third-row caveat must never be promoted to "Full Cabin".
      if (/does not imply third-row|does not imply third row/i.test(notes)) return 'selected_set';

      const seats = seatCount(variant, spec);
      if (seats >= 6) return 'full_3row_cabin';
      if (seats === 5) return 'full_cabin';
      if (/\bFull\b.*\bsets?\b/i.test(notes)) return seats >= 6 ? 'full_3row_cabin' : 'full_cabin';

      // A product with no three-row signal can safely treat a 2Pc+ selected set as full cabin.
      // 1Pc generic sets remain conservative unless they are a verified Full-Cab vehicle above.
      if (!context?.threeRow) return pieces === 1 ? 'selected_set' : 'full_cabin';

      // On a three-row product, 4Pc+ selected sets are the established full-cabin pattern.
      if (pieces >= 4) return 'full_3row_cabin';

      // Q8/SQ8 is the two-row branch of the shared Q7/Q8 product family.
      if (/\bQ8\b|\bSQ8\b/i.test(text)) return 'full_cabin';

      return 'selected_set';
    }

    // Before the fitment catalog finishes loading, keep only explicit title-based labels.
    return 'selected_set';
  }
  const coverageLabel = (scope) => ({
    full_cabin: 'Full Cabin',
    full_3row_cabin: 'Full 3-Row Cabin',
    full_cab: 'Full Cab',
    front_row: 'Front Row',
    driver_side: 'Driver Side',
    passenger_side: 'Passenger Side',
    rear_row: 'Rear Row',
    second_row: 'Second Row',
    third_row: 'Third Row',
    rows_1_2: '1st & 2nd Rows',
    rows_2_3: '2nd & 3rd Rows',
    cargo_area: 'Cargo Area'
  })[scope] || '';

  function productName(variant, spec = null, context = {}, productTitle = '') {
    const pieces = pieceCount(variant, spec);
    const noun = pieces === 1 ? 'Mat' : 'Mats';
    const scope = coverageScope(variant, spec, context, productTitle);
    if (scope === 'full_cabin' || scope === 'full_3row_cabin') return 'Full-Cabin Carpet Floor ' + noun;
    if (scope === 'full_cab') return 'Full-Cab Carpet Floor ' + noun;
    if (scope === 'front_row') return 'Front-Row Carpet Floor ' + noun;
    if (scope === 'driver_side') return 'Driver-Side Carpet Floor ' + noun;
    if (scope === 'passenger_side') return 'Passenger-Side Carpet Floor ' + noun;
    if (scope === 'rear_row') return 'Rear-Row Carpet Floor ' + noun;
    if (scope === 'second_row') return 'Second-Row Carpet Floor ' + noun;
    if (scope === 'third_row') return 'Third-Row Carpet Floor ' + noun;
    if (scope === 'rows_1_2') return 'First- and Second-Row Carpet Floor ' + noun;
    if (scope === 'rows_2_3') return 'Second- and Third-Row Carpet Floor ' + noun;
    if (scope === 'cargo_area') return 'Cargo-Area Carpet ' + noun;
    return 'Carpet Floor ' + noun;
  }

  function displayFitmentLabel(variant, spec = null, context = {}, productTitle = '') {
    const raw = stripVariantColor(variant?.title);
    const scope = coverageScope(variant, spec, context, productTitle);
    const label = coverageLabel(scope);
    if (!label || scope === 'selected_set') return raw;

    const match = raw.match(/\b(\d+)\s*-?\s*Pc(?:\s+Set)?\b/i);
    if (!match) return raw;

    const pieces = pieceCount(variant, spec) || Number(match[1]);
    const pieceLabel = pieces === 1 ? '1-Pc' : pieces + '-Pc Set';
    let prefix = raw.slice(0, match.index).trim().replace(/\s*[\u00b7\u2022]\s*$/, '').trim();
    let detail = raw.slice(match.index + match[0].length).trim();

    if (scope === 'front_row') prefix = prefix
      .replace(/(?:\s*[\u00b7\u2022]\s*|\s+)Front\s*Row\s*$/i, '')
      .replace(/\s+Front\s*$/i, '');
    if (scope === 'full_cab') prefix = prefix
      .replace(/(?:\s*[\u00b7\u2022]\s*|\s+)Front\s*Row\s*$/i, '')
      .replace(/\s+Front\s*$/i, '');
    if (scope === 'rear_row') prefix = prefix.replace(/(?:\s*[\u00b7\u2022]\s*|\s+)Rear\s*(?:Row|Mat)\s*$/i, '');
    if (scope === 'second_row') prefix = prefix.replace(/(?:\s*[\u00b7\u2022]\s*|\s+)2nd\s+Rows?\s*$/i, '');
    if (scope === 'third_row') prefix = prefix.replace(/(?:\s*[\u00b7\u2022]\s*|\s+)3rd\s+Row\s*$/i, '');
    if (scope === 'rows_1_2') prefix = prefix.replace(/(?:\s*[\u00b7\u2022]\s*|\s+)1st\s*&\s*2nd\s+Rows?\s*$/i, '');
    if (scope === 'rows_2_3') prefix = prefix.replace(/(?:\s*[\u00b7\u2022]\s*|\s+)2nd\s*&\s*3rd\s+Rows?\s*$/i, '');
    if (scope === 'driver_side') prefix = prefix.replace(/\s+(?:Driver|Driver[- ]Side)\s*$/i, '');
    if (scope === 'passenger_side') prefix = prefix.replace(/\s+(?:Passenger|Passenger[- ]Side)\s*$/i, '');

    detail = detail.replace(/^[\s\u00b7\u2022,;:-]+/, '').trim();
    const wrapped = detail.match(/^\((.*)\)$/);
    if (wrapped) detail = wrapped[1].trim();

    const conditionText = value => String(value || '').toLowerCase().replace(/center heat vent/g, 'center heating outlet').replace(/[^a-z0-9]+/g, ' ').trim();
    const rawConditions = conditionText(raw);
    const required = (spec?.conditions || []).filter(condition => !rawConditions.includes(conditionText(condition)));
    return [prefix, label, pieceLabel, detail, ...required].filter(Boolean).join(' · ');
  }

  function coverageDescription(variant, spec = null, context = {}, productTitle = '') {
    const scope = coverageScope(variant, spec, context, productTitle);
    const label = coverageLabel(scope);
    const pieces = pieceCount(variant, spec);
    if (!label || scope === 'selected_set') return spec?.coverage || '';
    return label + (pieces ? '; ' + pieces + ' ' + (pieces === 1 ? 'piece' : 'pieces') : '');
  }
  const genericTrim = (value) => /all\s+(?:factory\s+)?trims|sharing\s+the\s+selected\s+floor\s+pattern|configuration\s+(?:to\s+be|not)\s+confirmed/i.test(String(value || ''));
  function trimMapValid(record, selectionRanges) {
    const map = record?.year_trim_map;
    if (!map || typeof map !== 'object' || Array.isArray(map)) return false;
    const entries = Object.entries(map);
    return entries.length > 0 && entries.every(([year, trims]) => /^\d{4}$/.test(year) &&
      supports(selectionRanges, Number(year)) && Array.isArray(trims) && trims.length > 0 &&
      trims.every((trim) => typeof trim === 'string' && trim.trim() && !genericTrim(trim)));
  }
  const idOf = (value) => String(value ?? '').split('/').pop();
  function configurationKey(variant) {
    return String(variant?.title || '').replace(/\s*\/\s*(?:black|gr[ae]y)\s*$/i, '')
      .replace(/\b(?:front|rear)\s*(?:row|rows|mat|mats)?\b/gi, '')
      .replace(/\b(?:1st|2nd|3rd)(?:\s*&\s*(?:2nd|3rd))?\s*rows?\b/gi, '')
      .replace(/\b\d+\s*-?\s*pc\s*(?:set)?\b/gi, '')
      .replace(/\bdriver(?:[- ]side|'s)?\s*(?:mat)?|\bpassenger(?:[- ]side|'s)?\s*(?:mat)?/gi, '')
      .replace(/\s*\((?:black|gr[ae]y)\)\s*$/i, '').replace(/[\u00b7\s]+/g, ' ').trim().replace(/^(?:black|gr[ae]y)$/i, '').toLowerCase();
  }
  function resolve(records, handle, variant, variants, title, utils) {
    const ownRanges = utils.variantRanges(variant, title);
    const sameProduct = records.filter((r) => r && r.product_handle === handle);
    const hasBinding = (r) => r.variant_id != null || Array.isArray(r.variant_ids);
    const bound = sameProduct.filter((r) => [r.variant_id, ...(Array.isArray(r.variant_ids) ? r.variant_ids : [])]
      .filter((id) => id != null).some((id) => idOf(id) === idOf(variant.id)));
    const matchesYears = (r) => ownRanges.some(([a,b]) => a === Number(r.year_start) && b === Number(r.year_end));
    if (bound.length) return bound.length === 1 && matchesYears(bound[0])
      ? {record:bound[0], binding:'explicit'} : {record:null, binding:'ambiguous'};
    const candidates = sameProduct.filter((r) => !hasBinding(r) && matchesYears(r));
    if (candidates.length !== 1) return {record:null, binding:candidates.length ? 'ambiguous' : 'missing'};
    const peers = variants.filter((v) => utils.variantRanges(v, title).some(([a,b]) =>
      ownRanges.some(([x,y]) => x === a && y === b)));
    if (new Set(peers.map(configurationKey)).size > 1) return {record:null, binding:'ambiguous'};
    return {record:candidates[0], binding:'legacy_catalog'};
  }

    function selectedLabel(variant, productTitle) {
      const text = String(variant.title || '').trim();
      const family = productTitle.replace(/^.*?\bfor\s+/i,'').replace(/,\s*(?:0\.\d|1\.\d).*$/i,'');
      if (/^(?:default title|black|gr[ae]y)$/i.test(text)) return family + (text === 'Default Title' ? '' : ' · ' + text);
      const makes = ['Mercedes-Benz','Land Rover','Alfa Romeo','BMW','Audi','Cadillac','Chevrolet','Chrysler','Dodge','Ford','Genesis','GMC','Honda','Infiniti','Ineos','Jeep','Kia','Lexus','Lincoln','Maserati','Mazda','MINI','Nissan','Porsche','Ram','Subaru','Tesla','Toyota','Volkswagen','Volvo','Fiat','Abarth','GWM','Jetour','Freightliner','Kenworth','Peterbilt','Westfalia','EZGO'];
      const present = makes.filter((make) => new RegExp('\\b' + make + '\\b','i').test(productTitle));
      if (present.length !== 1 || new RegExp('\\b' + present[0] + '\\b','i').test(text)) return text;
      return text.replace(/^(\d{4}(?:\s*[-\u2010-\u2015]\s*\d{4})?)\s+/, '$1 ' + present[0] + ' ');
    }

  const api = { version: '20261001-final-regression-v4', ranges, supports, variantRanges, defaultOption, pieceCount, seatCount, verifiedLegacySelectedScope, coverageScope, coverageLabel, coverageDescription, displayFitmentLabel, productName, genericTrim, trimMapValid, configurationKey, selectedLabel, resolveRecord: (records, handle, variant, variants, title) => resolve(records, handle, variant, variants, title, api) };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') {
    window.WennebirdFitment = api;
    window.dispatchEvent(new Event('wb:fitment-utils-ready'));
  }
})();

/* Carry an exact model collection into its product cards; brand collections are unchanged. */
(() => {
  'use strict';
  const groups = {"Lexus GX":["Lexus GX 460","Lexus GX 550"],"Lexus LX":["Lexus LX 600"],"Mercedes-Benz EQE":["Mercedes-Benz EQE Sedan","Mercedes-Benz EQE SUV"],"Mercedes-Benz EQS":["Mercedes-Benz EQS Sedan","Mercedes-Benz EQS SUV"],"Audi A5":["Audi A5 Sportback"],"Land Rover Defender":["Land Rover Defender 110"],"Chevrolet Silverado":["Chevrolet Silverado 1500","Chevrolet Silverado 2500 HD","Chevrolet Silverado 3500 HD"],"Volkswagen Golf":["Volkswagen Golf GTI","Volkswagen Golf R"],"Mazda MX-5":["Mazda MX-5 Miata"],"GMC Sierra":["GMC Sierra 1500","GMC Sierra 2500 HD","GMC Sierra 3500 HD"],"GMC Canyon Crew":["GMC Canyon"],"MINI Cooper":["MINI Hardtop 2 Door","MINI Hardtop 4 Door"],"Ford Super Duty":["Ford F-250","Ford F-350","Ford F-450"],"RAM 2500/3500":["Ram 2500","Ram 3500"]};
  if(typeof window!=='undefined')window.WennebirdVehicleFamilies=Object.fromEntries(Object.entries(groups).map(([name,members])=>[name.toLowerCase(),members]));
  if(typeof document==='undefined'||typeof location==='undefined')return;
  if(!/\/collections\//.test(location.pathname))return;
  const script=Array.from(document.scripts).find(s=>/wennebird-fitment-utils\.js/.test(s.src));if(!script)return;
  const key=s=>String(s).replace(/\s+/g,' ').trim().toLowerCase();
  const collectionGroups = {'ram 1500':['Ram 1500','Ram 1500 Classic']};
  function collectionRoutes(model, index, products) {
    const members = collectionGroups[key(model)];
    if (!members) return null;
    if (!Array.isArray(products?.prefixes) || !Array.isArray(products?.products)) throw Error('Invalid product directory');
    const routes = new Map();
    for (const item of index.items.filter(item => members.some(member => key(member) === key(item[1])))) {
      for (const [productIndex] of item[2] || []) {
        const row = products.products[productIndex];
        if (!row || typeof products.prefixes[row[0]] !== 'string' || typeof row[1] !== 'string') throw Error('Invalid product route');
        const handle = products.prefixes[row[0]] + row[1];
        if (!routes.has(handle)) routes.set(handle, new Set());
        routes.get(handle).add(item[1]);
      }
    }
    return routes;
  }
  function decorate(model, routes){
    const apply=(a)=>{try{const u=new URL(a.href,location.href);if(u.origin!==location.origin||!u.pathname.includes('/products/'))return;const handle=decodeURIComponent(u.pathname.split('/products/')[1].split('/')[0]);const supported=routes?.get(handle);const cardModel=supported?.has(model)?model:supported?.size===1?[...supported][0]:model;u.searchParams.set('vehicle_model',cardModel);u.searchParams.set('wb_vehicle',cardModel);if(a.dataset.wbPreserveVariant!=='true'){u.searchParams.delete('variant');u.searchParams.delete('option_values');}const p=new URLSearchParams(location.search);for(const n of ['vehicle_year','preview_theme_id','pb'])if(p.has(n))u.searchParams.set(n,p.get(n));a.href=u.toString();}catch{}};
    const update=()=>document.querySelectorAll('main .product-grid a[href*="/products/"]').forEach(apply);update();
    document.addEventListener('click',e=>{const a=e.target.closest('main .product-grid a[href*="/products/"]');if(a)apply(a);},true);
    let queued=false;new MutationObserver(()=>{if(!queued){queued=true;queueMicrotask(()=>{queued=false;update();});}}).observe(document.querySelector('main')||document.body,{childList:true,subtree:true});
    document.documentElement.dataset.wbCollectionModel=model;
  }
  async function run() {
    const heading = document.querySelector('main .collection-hero__title') || document.querySelector('main h1'); if (!heading) return;
    const title = heading.cloneNode(true);
    title.querySelectorAll('.visually-hidden, [hidden]').forEach(node => node.remove());
    const name = key(title.textContent.replace(/\s+(?:Carpet\s+)?Floor\s+Mats.*$/i, ''));
    const family = Object.keys(groups).find(value => key(value) === name);
    if (family) { decorate(family); return; }
    const embedded = document.querySelector('[data-wb-vehicle-catalog]');
    const applyIndex = (data, products) => {
      if (data?.version !== '20260928-r3' || !Array.isArray(data.items)) throw Error('Invalid vehicle directory');
      const matches = data.items.filter(item => key(item[1]) === name);
      if (matches.length === 1) decorate(matches[0][1], collectionRoutes(matches[0][1], data, products));
    };
    try { const data=JSON.parse(embedded?.textContent || 'null');applyIndex(data?.index,data?.products);return; } catch {}
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 8000);
    try {
      const url = new URL(embedded?.dataset.indexUrl || script.src, location.href);
      if (!/^https?:$/.test(url.protocol) || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) return;
      if (!embedded?.dataset.indexUrl) { url.pathname = url.pathname.replace(/[^/]+$/, 'wb-vehicle-index-v3.json'); url.search = ''; }
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw Error('Directory unavailable');
      const index = await response.json();
      let products;
      if (collectionGroups[name]) {
        const productsUrl = new URL(url);
        productsUrl.pathname = productsUrl.pathname.replace(/[^/]+$/, 'wb-vehicle-products-v3.json');
        productsUrl.search = '';
        const productsResponse = await fetch(productsUrl, {signal:controller.signal,credentials:'same-origin'});
        if (!productsResponse.ok) throw Error('Product directory unavailable');
        products = await productsResponse.json();
      }
      applyIndex(index, products);
    } catch { document.documentElement.dataset.wbCollectionContext = 'unavailable'; }
    finally { clearTimeout(timer); }
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',run,{once:true});else run();
})();
